import { ActivityGroupStatus, IssueStatus, type IProfession } from '@d-orca/shared/trusstor';
import { findCatalogProfession } from '../../ActivitiesPage/model/issueDisplay';
import type { IListItem } from '../../ActivitiesPage/model/listItem';
import { daysBetween } from '../../SchedulerPage/lib/timeScale';

/**
 * Pure model behind the Monitor's building layers (Workforce / Activities /
 * Issues). Both the rail list and the building's chips are the Activities &
 * Issues page's own items — the schedule picked in the top bar's activities,
 * the site's issues — kept to those located on a floor of the building.
 */

export type MonitorLayer = 'workforce' | 'activities' | 'issues';

/** The rail's filter pills; the building's chips follow the same pick. */
export type ActivityFilter = 'all' | 'overdue' | 'inProgress' | 'planned' | 'complete';
export type IssueFilter = 'all' | 'critical' | 'forReview' | 'overdue' | 'safety';
export const ACTIVITY_FILTERS: readonly ActivityFilter[] = [
  'all',
  'overdue',
  'inProgress',
  'planned',
  'complete',
];
export const ISSUE_FILTERS: readonly IssueFilter[] = ['all', 'critical', 'forReview', 'overdue', 'safety'];

export function countBy<F extends string, T>(
  filters: readonly F[],
  list: readonly T[],
  match: (item: T, filter: F) => boolean,
): Record<F, number> {
  const counts = {} as Record<F, number>;
  for (const filter of filters) counts[filter] = list.filter((item) => match(item, filter)).length;
  return counts;
}

/** A list item's activity pill: done first, then late, then started, else planned. */
function itemActivityBucket(item: IListItem): Exclude<ActivityFilter, 'all'> {
  if (item.status === 'done' || item.activityStatus === ActivityGroupStatus.complete) return 'complete';
  if (item.isOverdue) return 'overdue';
  if (item.activityStatus === ActivityGroupStatus.inProgress || (item.progressPercent ?? 0) > 0)
    return 'inProgress';
  return 'planned';
}

/** Whether an Activities & Issues list item sits under the activity pill `filter`. */
export function itemMatchesActivityFilter(item: IListItem, filter: ActivityFilter): boolean {
  return filter === 'all' || itemActivityBucket(item) === filter;
}

/** Whether an Activities & Issues list item sits under the issue pill `filter`. */
export function itemMatchesIssueFilter(item: IListItem, filter: IssueFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'critical':
      return item.isCritical;
    case 'forReview':
      return item.status === IssueStatus.FOR_REVIEW;
    case 'overdue':
      return item.isOverdue;
    case 'safety':
      return item.isSafety;
  }
}

/** One chip on a floor: a trade's items there. */
export interface ILayerChip {
  /** Trade id, or `''` for issues filed without a trade. */
  tradeId: string;
  color: string | null | undefined;
  contractor: string | null | undefined;
  count: number;
  /** Activities: share of the trade's areas on this floor that are complete (0–100). */
  progress: number;
  /** At least one item is overdue. */
  late: boolean;
  /** Issues: at least one is critical. */
  critical: boolean;
  /** The chip's own items, for its hover card. */
  items: IListItem[];
}

/** A trade's pill and contractor — for an activity from its task, for an issue from the site catalog. */
export interface IItemTrade {
  tradeId: string;
  color: string | null | undefined;
  contractor: string | null | undefined;
}

/**
 * An item's trade. An issue's comes from the catalog: its list-item
 * profession is built from a minimized profession and carries no colour or
 * contractor, so it must not short-circuit the lookup.
 */
export function itemTrade(item: IListItem, catalog: readonly IProfession[]): IItemTrade | undefined {
  const issue = item.raw.issue;
  if (issue) {
    if (!issue.profession?.tradeId) return undefined;
    const match = findCatalogProfession(issue.profession, catalog);
    return { tradeId: issue.profession.tradeId, color: match?.backgroundColor, contractor: match?.contractor };
  }
  if (!item.profession) return undefined;
  const { tradeId, backgroundColor, contractor } = item.profession;
  return { tradeId, color: backgroundColor, contractor };
}

/** An activity's areas on one floor, and how many of them are complete. */
export function areasOnFloor(
  item: IListItem,
  floorId: string,
  floorOfArea: ReadonlyMap<string, string>,
): { done: number; total: number } {
  const areas = (item.raw.activity?.locations ?? []).filter((area) => floorOfArea.get(area.locationId) === floorId);
  return { done: areas.filter((area) => area.status === 'complete').length, total: areas.length };
}

/** The building floors an item is located on, in no particular order. */
export function itemFloors(
  item: IListItem,
  floorOfArea: ReadonlyMap<string, string>,
  buildingFloors: ReadonlySet<string>,
): string[] {
  const ids =
    item.kind === 'activity'
      ? (item.raw.activity?.locations ?? []).map((area) => floorOfArea.get(area.locationId))
      : (item.raw.issue?.locations ?? []).map((location) => location.floor.floorId);
  return [...new Set(ids)].filter((id): id is string => id !== undefined && buildingFloors.has(id));
}

/**
 * floorId → one chip per trade, in first-seen order. An activity chip's progress
 * pools its trade's areas on that floor; an issue chip flags any critical one.
 */
export function chipsByFloor(
  items: readonly IListItem[],
  floorsOf: (item: IListItem) => string[],
  tradeOf: (item: IListItem) => IItemTrade | undefined,
  floorOfArea: ReadonlyMap<string, string>,
): Map<string, ILayerChip[]> {
  const byFloor = new Map<string, Map<string, ILayerChip & { done: number; total: number }>>();
  for (const item of items) {
    const trade = tradeOf(item);
    const key = trade?.tradeId ?? '';
    for (const floorId of floorsOf(item)) {
      const trades = byFloor.get(floorId) ?? new Map();
      byFloor.set(floorId, trades);
      const chip = trades.get(key) ?? {
        tradeId: key,
        color: trade?.color,
        contractor: trade?.contractor,
        count: 0,
        progress: 0,
        late: false,
        critical: false,
        items: [],
        done: 0,
        total: 0,
      };
      trades.set(key, chip);
      const areas = areasOnFloor(item, floorId, floorOfArea);
      chip.count += 1;
      chip.late ||= item.isOverdue;
      chip.critical ||= item.isCritical;
      chip.items.push(item);
      chip.done += areas.done;
      chip.total += areas.total;
    }
  }
  return new Map(
    [...byFloor].map(([floorId, trades]) => [
      floorId,
      [...trades.values()].map(({ done, total, ...chip }) => ({
        ...chip,
        progress: total ? Math.round((done / total) * 100) : 0,
      })),
    ]),
  );
}

/** Trusstor's main-page due-date sections, in display order. */
export type DueGroup = 'overdue' | 'today' | 'week' | 'upcoming' | 'noDate' | 'complete';
export const ACTIVITY_GROUPS: readonly DueGroup[] = ['overdue', 'today', 'week', 'upcoming', 'complete'];
export const ISSUE_GROUPS: readonly DueGroup[] = ['overdue', 'week', 'upcoming', 'noDate'];

/**
 * A list item's section. Late and complete go by status; the rest by due day —
 * an activity's today, the coming week or later; an issue's coming week
 * (today included), later, or no due date at all.
 */
export function itemDueGroup(item: IListItem, today: string): DueGroup {
  if (item.isOverdue) return 'overdue';
  if (item.kind === 'activity' && itemActivityBucket(item) === 'complete') return 'complete';
  if (!item.end) return 'noDate';
  const days = daysBetween(today, item.end);
  if (item.kind === 'activity' && days <= 0) return 'today';
  return days < 7 ? 'week' : 'upcoming';
}

/** `list` split into `order`'s sections, empty ones left out, item order kept. */
export function groupInto<T>(order: readonly DueGroup[], list: readonly T[], groupOf: (item: T) => DueGroup) {
  return order
    .map((key) => ({ key, items: list.filter((item) => groupOf(item) === key) }))
    .filter((group) => group.items.length > 0);
}
